import { useEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import { Icon } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Icon";
import { AnimatePresence, motion } from "framer-motion";

import { Video } from "@/components/atoms/Video";
import { Spinner } from "@/components/atoms/Spinner";
import { useClassNames } from "@/utils/classNames";
import { useVideoPlayer } from "@/utils/hooks/useVideoPlayer";
import { useDeviceDetection } from "@/utils/hooks/useDeviceDetection";
import { isModalOpenAtom } from "@/constants/atoms";
import { StreamingIcons } from "@/assets/iconFont/streaming-icons";

import {
  VideoPlayerProps,
  ScreenOrientationWithLock,
  OrientationLockType,
} from "./types";
import styles from "./VideoPlayer.module.scss";

/** Duration to display spinner before starting playback */
const SPINNER_DURATION = 2000; // ms

/**
 * Video Player organism
 * Can be displayed inline or as a fullscreen modal with landscape orientation lock
 *
 * @param props - The video player properties
 * @returns the video player component
 */
const VideoPlayer = ({
  videoProps,
  className,
  isModal = false,
  onClose,
}: VideoPlayerProps) => {
  const { videoRef, isPlaying, handlePlay, handlePause, handleEnded } =
    useVideoPlayer();
  const { isMobileDevice } = useDeviceDetection();
  const [isOpen, setIsOpen] = useAtom(isModalOpenAtom);
  const [showSpinner, setShowSpinner] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  const orientation = screen.orientation as unknown as
    | ScreenOrientationWithLock
    | undefined;

  /** Spinner and autoplay effect - handles spinner display and video playback timing */
  useEffect(() => {
    if (isModal && isOpen) {
      setShowSpinner(true);

      const player = videoRef.current;

      /** Hide spinner after fixed duration */
      const spinnerTimer = setTimeout(() => {
        setShowSpinner(false);
      }, SPINNER_DURATION);

      /** Attempt to play video after spinner duration */
      if (player) {
        const attemptPlay = () => {
          player.play().catch(() => {
            /** Autoplay was blocked, ignore */
          });
        };

        /** Wait for spinner duration before starting playback */
        const playTimer = setTimeout(attemptPlay, SPINNER_DURATION);
        const retryTimer = setTimeout(attemptPlay, SPINNER_DURATION + 300);

        return () => {
          clearTimeout(spinnerTimer);
          clearTimeout(playTimer);
          clearTimeout(retryTimer);
        };
      }

      return () => clearTimeout(spinnerTimer);
    }
  }, [isModal, isOpen]);

  /** Modal-specific effects - fullscreen and orientation */
  useEffect(() => {
    if (isModal && isOpen) {
      /** Request fullscreen and lock orientation for Android/mobile compatibility */
      const setupFullscreenAndOrientation = async () => {
        try {
          /** Step 1: Request fullscreen only on mobile devices (required for orientation lock on Android) */
          if (
            isMobileDevice &&
            modalRef.current &&
            document.fullscreenEnabled
          ) {
            await modalRef.current.requestFullscreen();
          }

          /** Step 2: Lock screen orientation to landscape */
          if (orientation?.lock) {
            await orientation.lock(OrientationLockType.Landscape);
          }
        } catch (err) {
          console.warn("Failed to setup fullscreen/orientation:", err);
        }
      };

      setupFullscreenAndOrientation();
    } else if (isModal && !isOpen) {
      /** Exit fullscreen and restore orientation when modal closes */
      const cleanup = async () => {
        /** Exit fullscreen if active */
        if (document.fullscreenElement) {
          try {
            await document.exitFullscreen();
          } catch (err) {
            console.warn("Failed to exit fullscreen:", err);
          }
        }

        /** Restore orientation to portrait-primary */
        if (orientation?.lock) {
          try {
            await orientation.lock(OrientationLockType.PortraitPrimary);
          } catch (err) {
            console.warn("Failed to restore orientation to portrait:", err);
          }
        }
      };

      cleanup();
    }
  }, [isModal, isOpen, orientation]);

  const modalOverlayClasses = useClassNames(styles, styles["modal-overlay"]);

  const videoPlayerClasses = useClassNames(
    styles,
    styles["video-player"],
    className,
  );

  const videoPlayerContent = (
    <div className={styles["video-container"]}>
      {showSpinner && (
        <div className={styles["spinner-overlay"]}>
          <Spinner />
        </div>
      )}
      <Video {...videoProps} ref={videoRef} />
    </div>
  );

  /** Render as modal */
  if (isModal) {
    return (
      <AnimatePresence
        onExitComplete={() => {
          if (onClose) {
            onClose();
          }
        }}
      >
        {isOpen && (
          <motion.div
            ref={modalRef}
            className={modalOverlayClasses}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              className={styles["close-button"]}
              onClick={() => setIsOpen(false)}
              aria-label="Close video player"
            >
              <Icon icon={StreamingIcons.Close} />
            </button>
            <div
              className={styles["video-player-container"]}
              onClick={(e) => e.stopPropagation()}
            >
              {videoPlayerContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  /** Render inline */
  return <div className={videoPlayerClasses}>{videoPlayerContent}</div>;
};

export { VideoPlayer };
